import useEcomProduct from '@/hooks/useEcomProduct';
import dlv from 'dlv';
import is from './is';
import { ECOM_DEFAULT_QUANTITY } from '@/configs/constants';
import { Discount } from '@/pages/ecom/cart';

export const populatePdpEcomGTM = (productDetail: any, category: any, product: any) => {
  const { materialId, price, plainName, brand } = productDetail;
  const { name: brandName } = dlv(brand, 'data.attributes', null) ?? {};
  const { coupon, applicableDisc } = dlv(product, 'appliedCoupon') ?? {};
  const { sellingPrice = '' } = price;
  const quantity = is.null(productDetail.quantity)
    ? ECOM_DEFAULT_QUANTITY.min
    : productDetail.quantity?.min;
  return {
    currency: 'INR',
    value: quantity * sellingPrice || sellingPrice,
    items: [
      {
        item_id: materialId,
        item_name: plainName,
        coupon: coupon,
        discount: applicableDisc ?? 0,
        item_brand: brandName,
        item_category: category.name,
        price: sellingPrice,
        quantity: quantity,
      },
    ],
  };
};

export const populateCartEcomGTM = (cartData: any) => {
  const cart = dlv(cartData, 'data.data', {}) ?? {};
  const { items, discountList, payableAmount, totalAmount } = cart;

  const coupon = getCouponFromList(discountList);
  const prexo = getPrexoFromList(discountList);

  return {
    cart_value: payableAmount,
    discount: totalAmount - payableAmount,
    items: items?.map((item: any) => {
      return {
        product_name: item.item.name,
        product_id: item.item.materialId,
        price: item.item.sellingPrice,
      };
    }),
    exchange_discount: prexo?.applicableDisc,
    totalCartItems: items?.length,
    coupon: coupon?.coupon,
    productID: items?.map((item: any) => item.item.materialId).join(','),
    couponCode: coupon?.coupon,
    couponValue: coupon?.applicableDisc,
    ecommerce: getEcommerceDtoFromCart(cart),
  };
};

export const populateCheckoutEcomGTM = (cart: any) => {
  const { items, discountList, totalAmount, payableAmount } = cart;
  const coupon = getCouponFromList(discountList);
  const prexo = getPrexoFromList(discountList);

  return {
    exchange_discount: prexo?.applicableDisc,
    totalCartItems: items?.length,
    productID: items?.map((item: any) => item?.item?.materialId).join(','),
    couponCode: coupon?.coupon,
    couponValue: coupon?.applicableDisc,
    exchange_applied: !!prexo?.applicableDisc,
    couponApplied: !!coupon?.coupon,
    coupon: coupon?.coupon,
    items: items?.map((item: any) => {
      return {
        product_name: item.item.name,
        product_id: item.item.materialId,
        price: item.item.sellingPrice,
      };
    }),
    total_value: payableAmount,
    discount: totalAmount - payableAmount,
    ecommerce: getEcommerceDtoFromCart(cart),
  };
};

export const getEcommerceDtoFromCart = (cart: any) => {
  const { payableAmount, items, discountList } = cart;

  const coupon = getCouponFromList(discountList);

  return {
    currency: 'INR',
    value: payableAmount,
    coupon: coupon?.coupon,
    items: items?.map((item: any) => {
      const cartItem = dlv(item, 'item', {}) ?? {};
      const discountItems = dlv(item, 'discountItems', []) ?? [];
      const { materialId, sellingPrice, quantity, name } = cartItem;
      const coupon = getCouponFromList(discountItems);
      return {
        item_id: materialId,
        item_name: name,
        coupon: coupon?.coupon,
        discount: coupon?.applicableDisc,
        price: sellingPrice,
        quantity: quantity,
      };
    }),
  };
};

const getPrexoFromList = (discountList: any) => {
  const findPrexoDiscountIndex = (discountItems: any): number => {
    if (!discountItems) {
      return -1;
    }
    return discountItems.findIndex(
      (discount: Discount) => discount.discountType?.toLowerCase() === 'prexo',
    );
  };
  const index = findPrexoDiscountIndex(discountList);

  return index === -1
    ? {}
    : {
        applicableDisc: discountList[index].applicableDisc,
      };
};

const getCouponFromList = (discountList: any) => {
  const findCouponDiscountIndex = (discountItems: any): number => {
    if (!discountItems) {
      return -1;
    }
    return discountItems.findIndex(
      (discount: Discount) => discount.discountType?.toLowerCase() === 'coupon',
    );
  };
  const index = findCouponDiscountIndex(discountList);
  return index === -1
    ? {}
    : {
        coupon: discountList[index].couponCode,
        applicableDisc: discountList[index].applicableDisc,
      };
};
